import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { CheckCircle2, AlertTriangle, ShieldCheck, UploadCloud, Download } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (s) => (s >= 90 ? "text-emerald-600" : s >= 70 ? "text-amber-600" : "text-red-600");
const barColor = (p) => (p >= 90 ? "bg-emerald-500" : p >= 70 ? "bg-amber-500" : "bg-red-500");

export function DataHealthPanel() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/portfolio/data-health").then((r) => setH(r.data)).catch(() => {});
  }, []);

  const exportUnknown = async () => {
    setBusy(true);
    try {
      const r = await api.get("/portfolio/unknown-dpd/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "recoveros_unknown_dpd_accounts.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported. Fill in Payment Due Date and re-import to fix DPD.");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!h || h.total === 0) return null;

  return (
    <div className="border border-border bg-card p-5 mb-6" data-testid="data-health-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#0B3D91]" />
          <div className="font-heading font-bold text-lg">Portfolio Data Health</div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-black font-heading mono ${scoreColor(h.quality_score)}`} data-testid="data-health-score">{h.quality_score}%</div>
          <div className="text-[11px] text-muted-foreground -mt-1">Import Quality Score</div>
        </div>
      </div>

      {/* Mandatory field completeness */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4">
        {h.fields.map((f) => (
          <div key={f.key} data-testid={`data-health-field-${f.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="mono font-semibold">{f.present_pct}%{f.missing > 0 ? ` · ${fmtNum(f.missing)} missing` : ""}</span>
            </div>
            <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
              <div className={`h-full ${barColor(f.present_pct)}`} style={{ width: `${f.present_pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Unknown DPD nudge */}
      {h.dpd.unknown > 0 && (
        <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm mb-4" data-testid="data-health-unknown-dpd">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong>{fmtNum(h.dpd.unknown)}</strong> account(s) ({h.dpd.unknown_pct}%) have <strong>Unknown DPD</strong> — no Payment Due Date. Upload valid due dates to improve Money Stuck™ &amp; Health Score accuracy.
            <button onClick={() => nav("/import")} className="ml-1 underline font-semibold inline-flex items-center gap-1" data-testid="data-health-fix-btn"><UploadCloud className="h-3.5 w-3.5" />Re-import</button>
          </span>
        </div>
      )}

      {/* Feature readiness */}
      <div className="flex flex-wrap gap-2" data-testid="data-health-features">
        {h.features.map((ft) => (
          <span key={ft.name} title={ft.reason}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${ft.ready ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
            {ft.ready ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {ft.name}
          </span>
        ))}
      </div>
    </div>
  );
}
